import { useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom'
import {
  Box,
  Button,
  TextField,
  Typography,
  Link,
  Alert,
  Checkbox,
  FormControlLabel,
  Card,
  CardContent,
  Collapse,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material'
import { Login as LoginIcon } from '@mui/icons-material'
import { authApi } from '@api/endpoints/auth'
import { useAuthStore } from '@store/authStore'
import { APP_NAME } from '@config/app.config'
import { SocialLoginButtons } from '../components/SocialLoginButtons'
import { TokenDuration } from '@api/types/auth'

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
  rememberMe: z.boolean().optional(),
  sessionName: z.string().optional(),
  tokenDuration: z.union([
    z.literal(60),
    z.literal(360),
    z.literal(720),
    z.literal(1440),
    z.literal(10080),
    z.literal(43200),
  ]).optional(),
})

type LoginFormData = z.infer<typeof loginSchema>

// Token duration options for the dropdown
const tokenDurationOptions = [
  { value: 60, label: '1 Hour' },
  { value: 360, label: '6 Hours' },
  { value: 720, label: '12 Hours' },
  { value: 1440, label: '24 Hours (Recommended)' },
  { value: 10080, label: '7 Days' },
  { value: 43200, label: '30 Days' },
]

export default function LoginPage() {
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false)
  const { login, setMfaRequired } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()

  // Get referral code from location state (passed from share code page)
  const locationState = location.state as { returnUrl?: string; message?: string; referralCode?: string } | null
  const referralCode = locationState?.referralCode

  // Build register URL with referral code if present
  const registerUrl = referralCode ? `/register?code=${encodeURIComponent(referralCode)}` : '/register'

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      rememberMe: false,
      tokenDuration: 1440,
    },
  })

  const rememberMe = watch('rememberMe')

  const onSubmit = async (data: LoginFormData) => {
    setError(null)
    setIsLoading(true)

    try {
      // Prepare login request with optional fields
      const loginRequest = {
        email: data.email,
        password: data.password,
        ...(data.sessionName && { sessionName: data.sessionName }),
        ...(data.rememberMe && data.tokenDuration && { tokenDuration: data.tokenDuration as TokenDuration }),
      }

      const response = await authApi.login(loginRequest)

      if (response.mfaRequired && response.mfaToken) {
        // MFA required - redirect to MFA page
        setMfaRequired(true, response.mfaToken)
        navigate('/mfa-verify')
      } else if (response.success && response.token && response.user) {
        // Login successful - store tokens
        const refreshToken = response.refreshToken || ''
        const expiresAt = response.expiresAt || ''
        const refreshTokenExpiresAt = response.refreshTokenExpiresAt || ''

        login(response.token, refreshToken, response.user, expiresAt, refreshTokenExpiresAt)

        // Check for pending share code redirect
        const pendingShareCode = localStorage.getItem('pendingShareCode')
        if (pendingShareCode) {
          localStorage.removeItem('pendingShareCode')
          navigate(`/code/${pendingShareCode}`, { replace: true })
        } else {
          // Redirect to the page they came from or adventures
          const from = (location.state as any)?.from?.pathname || '/adventures'
          navigate(from, { replace: true })
        }
      } else {
        setError(response.message || 'Login failed')
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Login failed. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSocialLogin = (provider: string) => {
    // Redirect to OAuth provider
    const returnUrl = window.location.origin + '/auth/callback'
    authApi.initiateExternalLogin(provider, returnUrl)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '80vh',
      }}
    >
      <Card sx={{ maxWidth: 450, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          {/* Closed Testing Banner */}
          <Alert severity="info" sx={{ mb: 3 }}>
            This site is currently in <strong>closed testing</strong> and is not open for public participation yet.
          </Alert>

          <Typography variant="h4" gutterBottom align="center" sx={{ mb: 3 }}>
            Login to {APP_NAME}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit(onSubmit)}>
            <TextField
              {...register('email')}
              label="Email"
              type="email"
              fullWidth
              margin="normal"
              error={!!errors.email}
              helperText={errors.email?.message}
              autoComplete="email"
            />

            <TextField
              {...register('password')}
              label="Password"
              type="password"
              fullWidth
              margin="normal"
              error={!!errors.password}
              helperText={errors.password?.message}
              autoComplete="current-password"
            />

            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={<Checkbox {...register('rememberMe')} />}
                label="Remember me"
              />

              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                sx={{ ml: 2 }}
              >
                {showAdvancedOptions ? 'Hide' : 'Show'} advanced options
              </Link>
            </Box>

            <Collapse in={showAdvancedOptions || rememberMe}>
              <Box sx={{ mt: 2 }}>
                {rememberMe && (
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="token-duration-label">Session Duration</InputLabel>
                    <Controller
                      name="tokenDuration"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          labelId="token-duration-label"
                          label="Session Duration"
                        >
                          {tokenDurationOptions.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                  </FormControl>
                )}

                <TextField
                  {...register('sessionName')}
                  label="Session Name (Optional)"
                  fullWidth
                  margin="normal"
                  placeholder="e.g., My iPhone, Work Laptop"
                  helperText="Give this session a name to identify it later"
                />
              </Box>
            </Collapse>

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={isLoading}
              startIcon={<LoginIcon />}
              sx={{ mt: 2, mb: 2 }}
            >
              {isLoading ? 'Logging in...' : 'Login'}
            </Button>

            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Link component={RouterLink} to="/forgot-password">
                Forgot password?
              </Link>
            </Box>

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2">
                Don't have an account?{' '}
                <Link component={RouterLink} to={registerUrl}>
                  Register here
                </Link>
              </Typography>
            </Box>
          </form>

          {/* Social Login */}
          <SocialLoginButtons onProviderClick={handleSocialLogin} isLoading={isLoading} />
        </CardContent>
      </Card>
    </Box>
  )
}
